import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Skeleton, Space, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import type { ReportCardData } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Report card builder.
 * Renders a real report card composed from marks. Print CSS makes it paper-
 * ready (U-15). Fee status is deliberately absent from the printed doc (M-10).
 *
 * Route: /admin/reportcards/:code?student=<admission_number>
 */
export function ReportCardBuilderPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const [sp] = useSearchParams();
  const admissionNumber = sp.get('student') ?? '';
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['reportcard', institution, code, admissionNumber],
    queryFn: () => productApi.getReportCard(institution, code, admissionNumber),
    enabled: !!institution && !!code && !!admissionNumber,
    retry: false,
  });

  if (!admissionNumber) {
    return (
      <Alert
        type="info"
        showIcon
        message="Pick a student first"
        description={
          <span>
            Open <a onClick={() => navigate(`/i/${institution}/admin/exams/${code}/results`)}>Results</a> and press "Report card" on any student.
          </span>
        }
      />
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div className="rc-no-print" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/exams/${code}/results`)}>← Results</Button>
        <div style={{ flex: 1 }} />
        <Button icon={<PrinterOutlined />} onClick={() => window.print()} type="primary">Print (A4)</Button>
      </div>

      {q.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {q.isError && <Alert type="error" showIcon message="Could not load report card" description={String(q.error)} />}
      {q.data && <ReportCard data={q.data} />}

      <style>{PRINT_CSS}</style>
    </Space>
  );
}

function ReportCard({ data }: { data: ReportCardData }) {
  const p = data;
  return (
    <div className="rc-page">
      <header className="rc-header">
        <div>
          <div className="rc-inst-name">{p.institution.name}</div>
          <div className="rc-inst-type">{p.institution.type.toUpperCase()}</div>
        </div>
        <div className="rc-doc-title">
          <div>Report Card · {p.exam.name}</div>
          <div className="rc-session">Session {p.exam.session_label} · {p.exam.exam_type.replace(/_/g, ' ')}</div>
        </div>
      </header>

      <section className="rc-student">
        <div><span className="rc-label">Name</span><span className="rc-val">{p.student.name}</span></div>
        <div><span className="rc-label">Adm. no.</span><span className="rc-val rc-mono">{p.student.admission_number}</span></div>
        <div><span className="rc-label">Grade</span><span className="rc-val">{p.student.grade}{p.student.section ? `-${p.student.section}` : ''}</span></div>
      </section>

      <table className="rc-marks">
        <thead>
          <tr>
            <th>Subject</th>
            <th>Code</th>
            <th>Date</th>
            <th className="rc-right">Max</th>
            <th className="rc-right">Passing</th>
            <th className="rc-right">Obtained</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {p.subjects.map((s) => (
            <tr key={s.subject_code + s.date}>
              <td>{s.subject_name}</td>
              <td className="rc-mono">{s.subject_code}</td>
              <td className="rc-mono">{s.date}</td>
              <td className="rc-right">{s.max_marks}</td>
              <td className="rc-right">{s.passing_marks}</td>
              <td className="rc-right">
                {s.absent ? '—' : s.marks_obtained ?? '—'}
              </td>
              <td className={`rc-result rc-${s.result}`}>{s.result}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="rc-total-label">Total</td>
            <td className="rc-right"><b>{p.totals.max}</b></td>
            <td></td>
            <td className="rc-right"><b>{p.totals.obtained}</b></td>
            <td className={`rc-result rc-${p.totals.result}`}>{p.totals.result} · {p.totals.percentage}%</td>
          </tr>
        </tfoot>
      </table>

      <footer className="rc-footer">
        <div className="rc-sig">
          <div className="rc-line" />
          <div>Class teacher</div>
        </div>
        <div className="rc-sig">
          <div className="rc-line" />
          <div>Principal</div>
        </div>
        <div className="rc-sig">
          <div className="rc-line" />
          <div>Guardian</div>
        </div>
      </footer>

      <div className="rc-fine">
        This report is a record of academic performance only. Fee status and other operational
        matters are documented separately (M-10).
      </div>
    </div>
  );
}

const PRINT_CSS = `
  .rc-page {
    background: #fff;
    color: #14213d;
    max-width: 794px;
    margin: 0 auto;
    padding: 28px 34px;
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    border: 1px solid #eeeef1;
    border-radius: 4px;
  }
  .rc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #14213d; padding-bottom: 12px; margin-bottom: 18px; }
  .rc-inst-name { font-size: 20px; font-weight: 700; }
  .rc-inst-type { font-size: 10px; letter-spacing: 1.5px; color: #5b6478; margin-top: 2px; }
  .rc-doc-title { text-align: right; font-size: 13px; font-weight: 600; }
  .rc-session { font-size: 11px; color: #5b6478; margin-top: 2px; font-weight: normal; }
  .rc-student { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 12px; margin-bottom: 16px; padding: 10px 12px; background: #f7f7f5; border-radius: 4px; }
  .rc-student > div { display: flex; flex-direction: column; }
  .rc-label { font-size: 10px; color: #8a94a6; letter-spacing: 0.4px; text-transform: uppercase; font-weight: 600; }
  .rc-val { font-size: 13px; margin-top: 2px; font-weight: 500; }
  .rc-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; }
  .rc-marks { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
  .rc-marks th, .rc-marks td { border: 1px solid #dcdde3; padding: 6px 8px; }
  .rc-marks th { background: #f7f7f5; text-align: left; font-weight: 600; }
  .rc-right { text-align: right; }
  .rc-result { text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.5px; font-weight: 600; }
  .rc-pass { color: #1c6442; }
  .rc-fail { color: #b3382f; }
  .rc-partial { color: #a35800; }
  .rc-absent, .rc-pending { color: #5b6478; }
  .rc-total-label { text-align: right; font-weight: 600; }
  .rc-marks tfoot td { background: #f7f7f5; }
  .rc-footer { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 40px; margin-bottom: 20px; }
  .rc-sig { text-align: center; font-size: 11px; color: #5b6478; }
  .rc-line { border-top: 1px solid #14213d; margin: 30px 20px 6px; }
  .rc-fine { font-size: 10px; color: #8a94a6; text-align: center; font-style: italic; }
  @media print {
    @page { size: A4; margin: 12mm; }
    body { background: #fff; }
    .rc-no-print, .ant-message, .ant-notification { display: none !important; }
    .rc-page { box-shadow: none; border: none; margin: 0; padding: 0; max-width: none; }
  }
`;
